import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import DateAndTimeStep from '../../4_Templates/DateAndTimeStep/DateAndTimeStep'
import ContactDetailsStep from '../../4_Templates/ContactDetailsStep/ContactDetailsStep'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideARBlock, displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'

function BookTestDrive({
  formExtraParams,
  preDefinedVehicleData,
  preDefinedDealerData,
  preDefinedUserLocation,
  initStep,
  containerRef
}) {
  const { isTopSectionHidden, source, language, lastStepBottomContents, initStepTopContents, onlyEVDealers, isEmbeddedOnBnP, introductionText, formDisclaimer, modelCode, modelOption } = formExtraParams
  const maxStepNum = 4;

  // Convert boolean string to bool type.
  const isTopSectionHiddenFormatted = (isTopSectionHidden ? isTopSectionHidden.toLowerCase() === 'true' : false);
  const onlyEVDealersFormatted = (onlyEVDealers ? onlyEVDealers.toLowerCase() === 'true' : false);
  const isEmbeddedOnBnPFormatted = (isEmbeddedOnBnP ? isEmbeddedOnBnP.toLowerCase() === 'true' : false);

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(initStep)

  const [dealerID, setDealerID] = useState(formExtraParams.dealerId);

  const isSelectionPreOrder = () => {
    let isPreOrder = false;

    if(window.preOrder) {
        window.preOrder.forEach((item) => {
            if (item.modelCode === modelCode && item.modelOption === modelOption) {
                isPreOrder = true;
            }                
        });
    }

    return isPreOrder;
  }

  if (preDefinedVehicleData) {
    preDefinedVehicleData.modelCode = modelCode ? modelCode : '';
    preDefinedVehicleData.modelOption = modelOption ? modelOption : '';
    preDefinedVehicleData.isModelPreOrder = isSelectionPreOrder();
  }

  const [data, setData] = useState({
    vehicleData: preDefinedVehicleData,
    dealerData: preDefinedDealerData,
    dateAndTime: null,
    contactDetails: null
  })

  // Display top contents on the init step.
  useEffect(() => {
    if (initStep === 0) {
      displayOutsideComponents(initStepTopContents)
    }
    // Add the form-related vars to GTM dataLayer.
    pushToGTMDataLayer({ form: 'Book a Test Drive' });
    pushToGTMDataLayer({ formid: 'BaTD' });
    // If initStep is later than 0, handle analytics code for each skipped step.
    if (initStep > 0) {
      for (let step = 0; step < initStep; step++) {
        setupTrackingCodes(data, step);
      }
    }

    if (window.bnp_retailer_mode) {
      // Set the polling until we have a dealer vars with the information set as we should always have the dealer vars information
      const intervalRef = setInterval(()=>{
        const dealerVars = window.summaryCalculator.getDealerVars();
      
        if (dealerVars) {
          console.log(dealerVars);
          setDealerID(dealerVars.dealerId);
  
          clearInterval(intervalRef);
        }
      }, 200);
    }    
  }, [])

  useEffect(() => {
    // Update 'formstep' dataLayer var when currentStep is changed
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
    // Handle mz-page-top padding based on current step.
    const containerEl = containerRef.current;
    if (containerEl) {
      if (currentStep === 0) {
        containerEl.classList.add('mz-page-top__disabled');
      } else if (containerEl.classList.contains('mz-page-top__disabled')) {
        containerEl.classList.remove('mz-page-top__disabled');
      }
    }
  }, [currentStep])

  useEffect(() => {
    console.log('BookTestDrive - forced a redraw with', dealerID);
  }, [dealerID, data])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === 1) {
      // Hide BaTD Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display BaTD related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      displayOutsideARBlock(data.vehicleData?.model)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    // Scroll to the top of the screen.
    if (!isEmbeddedOnBnPFormatted) {
      scrollToTop();
    }
  }

  const handlePreviousStep = () => {
    if (currentStep === initStep) return;

    const previousStep = currentStep - 1;

    // Manage visibility of components outside of the react app.
    /*if (nextStep === 1) {
      // Hide BaTD Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display BaTD related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      displayOutsideARBlock(data.vehicleData?.model)
    }*/
    // Remove the selected data
    const newProps = {dealerData: null}

    const newData = { ...data, ...newProps }    
    setData(newData)

    setCurrentStep(previousStep)
    
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => previousStep < prevState ? previousStep : prevState)

    // Scroll to the top of the screen.
    if (!isEmbeddedOnBnPFormatted) {
      scrollToTop();
    }
  }

  // Refresh data and go back to init step.
  const refreshSteps = () => {
    setCurrentStep(0)
    setCurrentProgress(0)
    setData({})
    displayOutsideComponents(initStepTopContents)
    scrollToTop()
  }

  // Handle adding analytics tracking attributes.
  const setupTrackingCodes = async (data, completedStep) => {
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 0 && data.vehicleData && !isEmbeddedOnBnPFormatted) {
        const trackData = { modelCarlineName : data.vehicleData.model, modelYear : data.vehicleData.modelYear, msrp : data.vehicleData.msrp };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 1 && data.dealerData) {
        const trackData = { dealerName : data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 3 && data.contactDetails) {
        const trackData = { email : data.contactDetails.email, firstname: data.contactDetails.firstname, lastname : data.contactDetails.lastname, postalcode : data.contactDetails.postalcode, optin : data.contactDetails.optin === 'on' ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'Contact Details Submit');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div
      className={`mz-book-test-drive ${isEmbeddedOnBnPFormatted ? 'mz-book-test-drive--bnp' : ''}`}
      data-current-step={currentStep}
    >
      {!isTopSectionHiddenFormatted && (
        <FormStepsNav
          currentStep={currentStep}
          currentProgress={currentProgress}
          setCurrentStep={setCurrentStep}
          refreshSteps={refreshSteps}
          isActive={(currentStep > 0 && currentStep < maxStepNum)}
          refreshText={languageController.getCopy('global', 'vehicles')}
          navList={[
            {
              step: 1,
              label: languageController.getCopy('global', 'selectDealerNav'),
            },
            {
              step: 2,
              label: languageController.getCopy('global', 'selectDateAndTimeNav'),
            },
            {
              step: 3,
              label: languageController.getCopy('global', 'contactDetailsNav'),
            },
          ]}
        />
      )}
      <div className={`mz-book-test-drive__steps ${isTopSectionHiddenFormatted ? 'mz-book-test-drive__steps--no-steps-nav' : ''}`}>
        {/* Step 0: Select a vehicle */}
        <StepWrapper
          className={`mz-book-test-drive__select-vehicle`}
          stepNum={0}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectVehicleStep
            title={languageController.getCopy('global', 'selectVehicle')}
            handleNextStep={handleNextStep}
            introductionText={introductionText}
          />
        </StepWrapper>
        {/* Step 1: Select a dealer */}
        <StepWrapper
          className={`mz-book-test-drive__select-dealer`}
          type="select-dealer"
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            vehicleData={data.vehicleData}
            title={languageController.getCopy('testDrive', 'step1Title')}
            subTitle={languageController.getCopy('global', 'findDealerSub')}
            isTopSectionHidden={isTopSectionHiddenFormatted}
            isEmbeddedOnBnP={isEmbeddedOnBnPFormatted}
            onlyEVDealers={onlyEVDealersFormatted}
            preDefinedUserLocation={preDefinedUserLocation}
            preDefinedDealerData={data.dealerData?.dealer}
            dealerId={dealerID}
            currentStep={currentStep}
            language={language}
            displayPrice={!isSelectionPreOrder()}
			      startingAt={languageController.getCopy('global', 'pricePrefix')}
          />
        </StepWrapper>
        {/* Step 2: Choose Date and Time */}
        <StepWrapper
          className={`mz-book-test-drive__date-and-time`}
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <DateAndTimeStep
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
            title={languageController.getCopy('global', 'selectDateAndTime')}
            subTitle={languageController.getCopy('testDrive', 'step2Subtitle')}
            isService={false}
          />
        </StepWrapper>
        {/* Step 3: Enter Contact Details */}
        <StepWrapper
          className={`mz-book-test-drive__contact-details`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ContactDetailsStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'fillContactForm')}
            submitButtonText={languageController.getCopy('testDrive', 'step4Submit')}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
            vehicleData={data.vehicleData}
            formSource={source}
            formPid={pidController.getPid('testDrive')}
            formEndPointUrl={getEndpointUrl('test-drive')}
            formLanguage={convertLangCodeToSingleLetter(language)}
            formDisclaimer={formDisclaimer}
          />
        </StepWrapper>
        {/* Step 4: Show Confirmation */}
        <StepWrapper
          className={`mz-book-test-drive__confirmation`}
          stepNum={4}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ConfirmationStep
            vehicleData={data.vehicleData}
            dateAndTime={data.dateAndTime}
            dealerData={data.dealerData}
            contactDetails={data.contactDetails}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

BookTestDrive.propTypes = {
  formExtraParams: PropTypes.object.isRequired,
  preDefinedVehicleData: PropTypes.object,
  preDefinedDealerData: PropTypes.object,
  preDefinedUserLocation: PropTypes.object,
  initStep: PropTypes.number.isRequired
}

BookTestDrive.defaultProps = {
  formExtraParams: {},
  preDefinedVehicleData: null,
  preDefinedDealerData: null,
  preDefinedUserLocation: null,
  initStep: 0
}

export default BookTestDrive